import { PlusCircleOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type {
  ColDef,
  GetRowIdParams,
  ICellRendererParams,
  IFilter,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { Drawer, message, Tooltip, Popover } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useMemo, useState, useCallback } from 'react'

import type { AutomaticAccessorial, Ratebook } from '../../../../../../api/useFreight/types'
import { useRateManagement } from '../../../../../../api/useFreight/useFreight'
import { ApprovalRenderer, ExcludeFromInvoiceRenderer } from '../ManualAccessorials/renderers'
import { CreateAccessorialForm, CreateAccessorialFormFooter } from './CreateAccessorialForm'
import { RateHistoryDrawer } from './RateHistoryDrawer'
import {
  ActiveRenderer,
  FeeTypeRenderer,
  LocationHierarchyRenderer,
  NameRenderer,
  NextRateRenderer,
  ProductGroupRenderer,
  RateRenderer,
  TriggerRenderer,
} from './renderers'
import { getFeeTypeUnit, getThresholdUnit, isTimeIntervalFeeType } from './utils'

export function AutomaticAccessorialsTab({ ratebook }: { ratebook: Ratebook }) {
  const { useAutomaticAccessorialsQuery } = useRateManagement()
  const { data, isLoading, error, refetch } = useAutomaticAccessorialsQuery(ratebook)
  const queryClient = useQueryClient()
  const { useCreateAutomaticAccessorialMutation } = useRateManagement()
  const createMutation = useCreateAutomaticAccessorialMutation()
  const [createDrawerVisible, setCreateDrawerVisible] = React.useState(false)
  const [rateHistoryDrawerVisible, setRateHistoryDrawerVisible] = React.useState(false)
  const [selectedAccessorialId, setSelectedAccessorialId] = React.useState<string | null>(null)
  const [editingAccessorial, setEditingAccessorial] = useState<AutomaticAccessorial | null>(null)

  const showDrawer = (e?: React.MouseEvent<HTMLElement>) => {
    setEditingAccessorial(null)
    setCreateDrawerVisible(true)
  }

  const closeDrawer = () => {
    setEditingAccessorial(null)
    setCreateDrawerVisible(false)
  }

  const handleCreateSuccess = () => {
    closeDrawer()
    refetch()
  }

  const showEditAccessorialDrawer = useCallback(
    (accessorialData: AutomaticAccessorial) => {
      const freshData = queryClient.getQueryData<AutomaticAccessorial[]>(['automaticAccessorials', ratebook._id])
      const currentAccessorial = freshData?.find((acc) => acc._id === accessorialData._id)

      setEditingAccessorial(currentAccessorial || accessorialData)
      setCreateDrawerVisible(true)
    },
    [queryClient, ratebook._id]
  )

  const showRateHistoryDrawer = useCallback((accessorialData: AutomaticAccessorial) => {
    setSelectedAccessorialId(accessorialData._id)
    setRateHistoryDrawerVisible(true)
  }, [])

  const closeRateHistoryDrawer = () => {
    setRateHistoryDrawerVisible(false)
    setSelectedAccessorialId(null)
  }

  // Get the current selected accessorial from the records
  const selectedAccessorial = React.useMemo(() => {
    if (!selectedAccessorialId || !data?.length) return null
    return data.find((acc) => acc._id === selectedAccessorialId) || null
  }, [data, selectedAccessorialId])

  const columnDefs: ColDef<AutomaticAccessorial>[] = useMemo(
    () => [
      {
        headerName: 'Active',
        field: 'disabled' as keyof AutomaticAccessorial,
        maxWidth: 100,
        minWidth: 100,
        pinned: 'left',
        cellRenderer: ActiveRenderer,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['active', 'inactive'],
          valueFormatter: (params: ValueFormatterParams) => (params.value === 'active' ? 'Active' : 'Inactive'),
        },
        filterValueGetter: (params: ValueGetterParams<AutomaticAccessorial>) =>
          params.data?.disabled ? 'inactive' : 'active',
      },
      {
        headerName: 'Name',
        field: 'name' as keyof AutomaticAccessorial,
        flex: 0,
        minWidth: 200,
        cellRenderer: NameRenderer,
        pinned: 'left',
      },
      {
        headerName: 'Destination',
        field: 'destinations' as keyof AutomaticAccessorial,
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => {
          const destinations = params.data?.destinations
          if (!destinations) return null
          const totalCount =
            (destinations.locations?.length ?? 0) +
            (destinations.site_states?.length ?? 0) +
            (destinations.site_regions?.length ?? 0) +
            (destinations.terminal_regions?.length ?? 0) +
            (destinations.terminal_states?.length ?? 0)
          return totalCount === 0 ? null : totalCount
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: { value: string }) => {
            if (!params.value) return 'Any'
            return `${params.value} selected`
          },
        },
        cellRenderer: (params: { data: AutomaticAccessorial }) => (
          <LocationHierarchyRenderer data={params.data} type='destinations' />
        ),
        width: 120,
        pinned: 'left',
      },
      {
        headerName: 'Product',
        field: 'product_group' as keyof AutomaticAccessorial,
        width: 160,
        flex: 0,
        pinned: 'left',
        cellRenderer: ProductGroupRenderer,
      },
      {
        headerName: 'Origin',
        field: 'origins' as keyof AutomaticAccessorial,
        width: 120,
        minWidth: 120,
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => {
          const origins = params.data?.origins
          if (!origins) return null
          const totalCount =
            (origins.locations?.length ?? 0) +
            (origins.site_states?.length ?? 0) +
            (origins.site_regions?.length ?? 0) +
            (origins.terminal_regions?.length ?? 0) +
            (origins.terminal_states?.length ?? 0)
          return totalCount === 0 ? null : totalCount
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: { value: string }) => {
            if (!params.value) return 'Any'
            return `${params.value} selected`
          },
        },
        cellRenderer: (params: { data: AutomaticAccessorial }) => (
          <LocationHierarchyRenderer data={params.data} type='origins' />
        ),
        pinned: 'left',
      },
      {
        headerName: 'Service Time',
        field: 'service_rule' as keyof AutomaticAccessorial,
        cellRenderer: (params: ICellRendererParams<AutomaticAccessorial>) => (
          <TriggerRenderer rule={params.data?.service_rule} />
        ),
        minWidth: 120,
        filter: 'agSetColumnFilter',
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => params.data?.service_rule?.type || '',
        filterParams: {
          valueFormatter: (params: { value: unknown }) => String(params.value || 'Any'),
        },
      },
      {
        headerName: 'Fee Type',
        field: 'fee_type' as keyof AutomaticAccessorial,
        cellRenderer: FeeTypeRenderer,
        width: 200,
        filter: 'agSetColumnFilter',
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => params.data?.fee_type?.type || '',
        filterParams: {
          valueFormatter: (params: { value: unknown }) => String(params.value || 'Any'),
        },
      },
      {
        headerName: 'Rate',
        field: 'rate' as keyof AutomaticAccessorial,
        cellRenderer: RateRenderer,
        type: 'numericColumn',
        cellStyle: { textAlign: 'right' },
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => {
          if (params.data?.rate === null || params.data?.rate === undefined) return null
          const feeType = params.data?.fee_type?.type || ''
          const isPercentageType = feeType.includes('%')

          // For percentage types, return the percentage value for filtering
          return isPercentageType ? Number((params.data.rate * 100).toFixed(3)) : Number(params.data.rate)
        },
        filterParams: {
          defaultOption: 'equals',
          allowedCharPattern: '\\d\\-\\.',
        },
      },
      {
        headerName: 'Threshold',
        field: 'fee_type' as keyof AutomaticAccessorial,
        width: 120,
        filter: 'agSetColumnFilter',
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => {
          const feeType = params.data?.fee_type
          if (!feeType || !('threshold' in feeType) || !feeType.threshold) return null
          return { type: feeType.type, threshold: feeType.threshold, interval: feeType.interval }
        },
        filterParams: {
          valueFormatter: (params: {
            value: { type: string; threshold: number }
          }) => {
            if (!params.value) return 'Any'
            return `${params.value.threshold}`
          },
        },
        cellRenderer: (params: ICellRendererParams<AutomaticAccessorial>) => {
          if (!params.value) return null
          const { type, threshold, interval } = params.value

          return (
            <Vertical style={{ overflow: 'visible', marginTop: -4 }}>
              <Texto style={{ lineHeight: '1' }} category='label' appearance='hint'>
                {getFeeTypeUnit(type, interval)} {isTimeIntervalFeeType(type) ? 'after' : 'over'}
              </Texto>
              <Texto appearance='primary'>
                <b>{threshold}</b> {getThresholdUnit(type)}
              </Texto>
            </Vertical>
          )
        },
      },
      {
        headerName: 'Max Charges',
        field: 'fee_type.max_intervals',
        initialHide: true,
      },
      {
        headerName: 'Next Rate',
        field: 'next_scheduled_rate',
        valueGetter: (params: ValueGetterParams<AutomaticAccessorial>) => {
          const date = params.data?.next_scheduled_rate?.effective_from
          return date ? new Date(date) : undefined
        },
        cellRenderer: NextRateRenderer,
        filter: 'agDateColumnFilter',
        filterParams: {
          browserDatePicker: true,
          minValidYear: 2000,
          maxValidYear: 2100,
          comparator: (filterDate: Date, cellValue: Date) => {
            if (!cellValue) return -1
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
      },
      {
        headerName: 'Approvals',
        field: 'requires_approval' as keyof AutomaticAccessorial,
        cellRenderer: ApprovalRenderer,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams) => (params.value ? 'REQ' : 'No'),
        },
      },
      {
        headerName: 'Exclude from Invoice',
        field: 'exclude_from_invoice' as keyof AutomaticAccessorial,
        cellRenderer: ExcludeFromInvoiceRenderer,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params: { value: boolean }) => (params.value ? 'REQ' : 'No'),
        },
      },
      {
        headerName: 'Actions',
        field: 'actions' as keyof AutomaticAccessorial,
        maxWidth: 100,
        minWidth: 100,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<AutomaticAccessorial>) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <Tooltip title='Edit'>
                <GraviButton
                  icon={<MaterialIcon size={16} className='pt-1' icon='edit' />}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (params.data) {
                      showEditAccessorialDrawer(params.data)
                    }
                  }}
                />
              </Tooltip>

              <Tooltip title='Rate Schedule'>
                <GraviButton
                  icon={<MaterialIcon size={16} className='pt-1' icon='overview' />}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (params.data) {
                      showRateHistoryDrawer(params.data)
                    }
                  }}
                />
              </Tooltip>
            </div>
          )
        },
      },
    ],
    [showEditAccessorialDrawer, showRateHistoryDrawer]
  )

  return (
    <Horizontal style={{ margin: '0px -1em', height: '100%' }}>
      <Vertical flex={1}>
        <GraviGrid<AutomaticAccessorial, IFilter>
          rowData={data}
          columnDefs={columnDefs}
          loading={isLoading}
          storageKey='automatic-accessorials-manage'
          controlBarProps={{
            title: 'Automatic Accessorials',
            actionButtons: (
              <Horizontal gap={8}>
                <GraviButton
                  icon={<PlusCircleOutlined />}
                  onClick={showDrawer}
                  buttonText='Create Automatic Accessorial'
                  theme2
                />
              </Horizontal>
            ),
          }}
          agPropOverrides={{
            getRowId: (params: GetRowIdParams<AutomaticAccessorial>) => params.data?._id || '',
            rowSelection: 'multiple',
            rowGroupPanelShow: 'onlyWhenGrouping',
            enableRangeSelection: true,
          }}
        />

        <Drawer
          title={editingAccessorial ? 'Edit Automatic Accessorial' : 'Create Automatic Accessorial'}
          placement='right'
          width={720}
          onClose={closeDrawer}
          open={createDrawerVisible}
          destroyOnClose
          footer={
            <CreateAccessorialFormFooter
              onCancel={closeDrawer}
              loading={createMutation.isPending}
              isEditing={!!editingAccessorial}
            />
          }
        >
          <CreateAccessorialForm
            ratebook={ratebook}
            onSuccess={handleCreateSuccess}
            editingAccessorial={editingAccessorial || undefined}
            createMutation={createMutation}
          />
        </Drawer>

        {rateHistoryDrawerVisible && selectedAccessorial && (
          <Drawer
            title={`Rate History - ${selectedAccessorial.name}`}
            placement='right'
            width={600}
            onClose={closeRateHistoryDrawer}
            open={rateHistoryDrawerVisible}
            destroyOnClose
          >
            <RateHistoryDrawer
              key={`rate-drawer-${selectedAccessorial._id}-${selectedAccessorial.rates?.length}`}
              accessorial={{
                ...selectedAccessorial,
                _id: selectedAccessorial._id as string,
                id: selectedAccessorial._id as string,
              }}
              onRateChange={async () => {
                await refetch()
              }}
            />
          </Drawer>
        )}
      </Vertical>
    </Horizontal>
  )
}
